import { decode, encode } from 'cbor2'
import { createWebSocketDispatcher, WebSocketDispatcher } from '../Dispatcher'
import { Id } from '../aliases'
import {
  agentWebSocketUrl,
  isLocalhost,
  localRequiresAuth,
  localhostLinkKey,
  localhostSessionId,
} from '../environment'
import AgentMessageTypes from './agentMessageTypes'
import AgentStatusCodes from './agentStatusCodes'
import { base64ToUint8, uint8ToBase64 } from '../utils'
import { CborMap, deserialize, serialize } from '@mesh/friendly-cbor-serializer'
import {
  NotificationPayload,
  RegisterClientResponseSchema,
  type ResponseHeader,
  ResponseHeaderSchema,
} from './messages'

const MeshinSubsystem = 1
const ClientTypeWeb = 1
const OsTypeMac = 1
const Aes256EncryptionType = 1
const LocalStorageKey = 'meshin-agent-config1'
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PrimarySessionIdKey = '_sessionIdKey'
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PrimaryLinkKey = '_linKey'

export type MeshAgent = {
  initialize: () => Promise<void>

  isAuthenticated: () => boolean

  isAnonymous: () => boolean

  addMeshOutNotificationListener: (
    listener: MeshOutNotificationListener,
  ) => void

  addSpaceMessageNotificationListener: (
    listener: SpaceMessageNotificationListener,
  ) => void

  setReauthenticationNeededListener: (listener: () => void) => void

  setReconnectedListener: (listener: () => void) => void

  buildAndSendMessage: (params: {
    payload: CborMap
    messageType: number
    messageId: Id
  }) => Promise<CborMap>
}

interface AgentConfig {
  linkKey?: CryptoKey
  sessionId?: Uint8Array
  clientId?: Uint8Array
  agentId?: Uint8Array
  authenticated?: boolean
  anonymousIdentityLinkCode?: Uint8Array
  isAnonymousSession?: boolean
}

interface AgentConfigExport {
  linkKey?: string
  sessionId?: string
  clientId?: string
  agentId?: string
  authenticated?: boolean
  anonymousIdentityLinkCode?: string
  isAnonymousSession?: boolean
  websocketUrl?: string
}

export interface MeshOutNotification extends NotificationPayload {}
export type MeshOutNotificationListener = (
  notification: MeshOutNotification,
) => void

export type SpaceMessageNotificationListener = (
  payload: Map<number, any>,
) => void

type MessageToSend = {
  messageId: Id
  payload: Id
}

async function exportAgentConfig(agentConfig: AgentConfig): Promise<void> {
  let config: AgentConfigExport = {
    sessionId: uint8ToBase64(agentConfig.sessionId),
    clientId: uint8ToBase64(agentConfig.clientId),
    agentId: uint8ToBase64(agentConfig.agentId),
    authenticated: agentConfig.authenticated,
    anonymousIdentityLinkCode: uint8ToBase64(
      agentConfig.anonymousIdentityLinkCode,
    ),
    isAnonymousSession: agentConfig.isAnonymousSession,
    websocketUrl: agentWebSocketUrl,
  }
  if (agentConfig.linkKey) {
    const exportedLinkKey = new Uint8Array(
      await crypto.subtle.exportKey('raw', agentConfig.linkKey),
    )
    config.linkKey = uint8ToBase64(exportedLinkKey)
  }
  localStorage.setItem(LocalStorageKey, JSON.stringify(config))
}

async function readAgentConfig(): Promise<AgentConfig | null> {
  let primarySessionId = localStorage.getItem(PrimarySessionIdKey) ?? ''
  let primaryLinkKey = localStorage.getItem(PrimaryLinkKey) ?? ''

  if (isLocalhost && localRequiresAuth) {
    console.log(
      'Running in localhost mode with auth required. Using primary credentials from environment variables.',
    )
    localStorage.removeItem(LocalStorageKey)
    localStorage.removeItem(PrimarySessionIdKey)
    localStorage.removeItem(PrimaryLinkKey)
    primarySessionId = localhostSessionId ?? ''
    primaryLinkKey = localhostLinkKey ?? ''
  }

  const configRead = localStorage.getItem(LocalStorageKey)

  // If we have primary credentials from the MeshIn companion (or localhost hardcoded), use them
  if (primarySessionId && primaryLinkKey) {
    const createNewConfig = async () => {
      return {
        sessionId: base64ToUint8(primarySessionId),
        linkKey: await importAesKey(base64ToUint8(primaryLinkKey)!),
      } as AgentConfig
    }

    if (!configRead) {
      return await createNewConfig()
    }
    const config = JSON.parse(configRead) as AgentConfigExport

    if (config.sessionId !== primarySessionId) {
      return await createNewConfig()
    }

    const agentConfig: AgentConfig = {
      sessionId: base64ToUint8(config.sessionId),
      clientId: base64ToUint8(config.clientId),
      agentId: base64ToUint8(config.agentId),
      authenticated: config.authenticated,
    }

    if (config.linkKey) {
      const linkKey = await importAesKey(base64ToUint8(config.linkKey)!)
      agentConfig.linkKey = linkKey
    }
    return agentConfig
  }

  // No primary credentials - try to restore a previously saved config (e.g. anonymous session)
  if (configRead) {
    const config = JSON.parse(configRead) as AgentConfigExport

    // Invalidate cached config if the WebSocket URL changed
    if (config.websocketUrl && config.websocketUrl !== agentWebSocketUrl) {
      localStorage.removeItem(LocalStorageKey)
      return null
    }

    const agentConfig: AgentConfig = {
      sessionId: base64ToUint8(config.sessionId),
      clientId: base64ToUint8(config.clientId),
      agentId: base64ToUint8(config.agentId),
      authenticated: config.authenticated,
      anonymousIdentityLinkCode: base64ToUint8(
        config.anonymousIdentityLinkCode,
      ),
      isAnonymousSession: config.isAnonymousSession,
    }

    if (config.linkKey) {
      const linkKey = await importAesKey(base64ToUint8(config.linkKey)!)
      agentConfig.linkKey = linkKey
    }
    return agentConfig
  }

  // No credentials at all - will need anonymous login
  return null
}
export function getDestinationAgentId(): Uint8Array {
  return new Uint8Array(32)
}

export function getSiteId(): Uint8Array {
  return new Uint8Array(32)
}

async function importAesKey(rawKey: Uint8Array): Promise<CryptoKey> {
  return await crypto.subtle.importKey('raw', rawKey, 'AES-GCM', true, [
    'encrypt',
    'decrypt',
  ])
}

async function buildMessage(
  messageId: Uint8Array,
  messageType: number,
  payloadObject: any,
  sessionId?: Uint8Array,
  linkKey?: CryptoKey,
): Promise<MessageToSend> {
  let payload = encode(payloadObject)
  if (linkKey) {
    const iv = crypto.getRandomValues(new Uint8Array(12))
    const ciphertextBuffer = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      linkKey,
      payload,
    )
    const ciphertext = new Uint8Array(ciphertextBuffer)
    const combined = new Uint8Array(iv.length + ciphertext.length)
    combined.set(iv, 0)
    combined.set(ciphertext, iv.length)
    payload = combined
  }
  const messageHeader = new Map()
  messageHeader.set(0, getDestinationAgentId())
  messageHeader.set(1, MeshinSubsystem)
  messageHeader.set(2, messageType)
  messageHeader.set(3, messageId)
  if (sessionId) {
    messageHeader.set(4, sessionId)
  }
  if (linkKey) {
    messageHeader.set(5, Aes256EncryptionType)
  }

  const message = new Map()
  message.set(0, messageHeader)
  message.set(1, payload)
  return {
    messageId,
    payload: encode(message),
  }
}

async function buildRegisterClientRequest(): Promise<MessageToSend> {
  const registerPayload = new Map()
  registerPayload.set(0, ClientTypeWeb)
  registerPayload.set(1, OsTypeMac)
  registerPayload.set(2, '0.16.0')
  const messageId = crypto.getRandomValues(new Uint8Array(32))
  return buildMessage(
    messageId,
    AgentMessageTypes.RegisterClientRequestType,
    registerPayload,
  )
}

async function buildAppPingRequest(
  linkKey: CryptoKey,
  sessionId: Uint8Array,
): Promise<MessageToSend> {
  const messageId = crypto.getRandomValues(new Uint8Array(32))
  return buildMessage(
    messageId,
    AgentMessageTypes.AppPingRequestType,
    new Map(),
    sessionId,
    linkKey,
  )
}

async function buildCreateSessionRequest(
  clientId: Uint8Array,
): Promise<MessageToSend> {
  const payload = new Map()
  payload.set(0, clientId)
  const messageId = crypto.getRandomValues(new Uint8Array(32))
  return buildMessage(
    messageId,
    AgentMessageTypes.CreateSessionRequestType,
    payload,
  )
}

async function buildAnonymousAuthenticateRequest(
  sessionId: Uint8Array,
  linkKey: CryptoKey,
  anonymousIdentityLinkCode?: Uint8Array,
): Promise<MessageToSend> {
  const anonymousIdentity = new Map()
  anonymousIdentity.set(0, anonymousIdentityLinkCode ?? null)
  const payload = new Map()
  payload.set(11, anonymousIdentity)
  const messageId = crypto.getRandomValues(new Uint8Array(32))
  return buildMessage(
    messageId,
    AgentMessageTypes.AuthenticateSessionRequestType,
    payload,
    sessionId,
    linkKey,
  )
}

function waitForSocketOpen(
  socket: WebSocket,
  onClose?: () => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    socket.onopen = () => {
      resolve()
    }
    socket.onerror = err => {
      console.error('WebSocket error:', err)
      reject(err)
    }
    socket.onclose = () => {
      if (onClose) {
        onClose()
      }
    }
  })
}

function getWebSocketUrl(): string {
  return agentWebSocketUrl
  //return "wss://localtest.mesh.in:8443/api";
}

export function createAgent(): MeshAgent {
  let agentConfig: AgentConfig = {}
  let socket: WebSocket | null = null
  let dispatcher: WebSocketDispatcher | null = null
  const notificationsListeners = new Map<
    number,
    Set<(notification: NotificationPayload) => void>
  >()

  let reauthNeededListener: (() => void) | null = null
  let reauthTimerId: number | null = null
  let reconnectedListener: (() => void) | null = null
  let initializingPromise: Promise<void> | null = null
  let connectingPromise: Promise<void> | null = null

  let intentionalClose = false
  let reconnectAttempt = 0
  let reconnectingPromise: Promise<void> | null = null
  let reconnectTimerId: ReturnType<typeof setTimeout> | null = null

  const MAX_RECONNECT_ATTEMPTS = 10
  const BASE_DELAY_MS = 1000
  const MAX_DELAY_MS = 30000

  function getReconnectDelay(attempt: number): number {
    const delay = Math.min(BASE_DELAY_MS * Math.pow(2, attempt), MAX_DELAY_MS)
    const jitter = delay * 0.2 * (Math.random() * 2 - 1)
    return Math.round(delay + jitter)
  }

  const setReauthenticationTimer = (seconds: number) => {
    if (reauthTimerId !== null) {
      clearTimeout(reauthTimerId)
    }
    reauthTimerId = window.setTimeout(async () => {
      await resetState(true)
      reauthNeededListener?.()
      reauthTimerId = null
    }, seconds * 1000)
  }

  const resetState = async (clearConfig: boolean) => {
    intentionalClose = true
    if (reconnectTimerId !== null) {
      clearTimeout(reconnectTimerId)
      reconnectTimerId = null
    }
    reconnectingPromise = null

    if (clearConfig) {
      agentConfig = {}
      await exportAgentConfig(agentConfig)
    }
    if (dispatcher) {
      dispatcher.close()
      dispatcher = null
    }
    if (socket) {
      socket.close()
      socket = null
    }
    intentionalClose = false
  }

  const connectSocket = async (): Promise<void> => {
    if (connectingPromise) return connectingPromise
    if (socket && socket.readyState === WebSocket.OPEN && dispatcher) {
      return
    }
    connectingPromise = (async () => {
      try {
        if (dispatcher) {
          dispatcher.close()
          dispatcher = null
        }
        if (socket) {
          intentionalClose = true
          socket.close()
          socket = null
          intentionalClose = false
        }

        const websocketUrl = getWebSocketUrl()
        if (!websocketUrl) {
          throw new Error('WebSocket URL not set')
        }

        socket = new WebSocket(websocketUrl)
        await waitForSocketOpen(socket, () => {
          if (intentionalClose) return
          if (dispatcher) {
            dispatcher.close()
            dispatcher = null
          }
          socket = null
          scheduleReconnect()
        })

        if (!socket) return

        dispatcher = createWebSocketDispatcher(socket)
        dispatcher.setNotificationsListener(listenNotifications)
      } finally {
        connectingPromise = null
      }
    })()
    return connectingPromise
  }

  const scheduleReconnect = (): void => {
    if (intentionalClose || reconnectingPromise) return
    if (reconnectAttempt >= MAX_RECONNECT_ATTEMPTS) {
      console.error('Max WebSocket reconnect attempts reached')
      return
    }

    const delay = getReconnectDelay(reconnectAttempt)
    console.log(
      `Scheduling WebSocket reconnect attempt ${reconnectAttempt + 1} in ${delay}ms`,
    )

    reconnectingPromise = new Promise<void>(resolve => {
      reconnectTimerId = setTimeout(async () => {
        reconnectTimerId = null
        try {
          await connectSocket()
          reconnectAttempt = 0
          console.log('WebSocket reconnected successfully')
          reconnectingPromise = null
          reconnectedListener?.()
          resolve()
        } catch (err) {
          console.error('WebSocket reconnect attempt failed:', err)
          reconnectAttempt++
          reconnectingPromise = null
          resolve()
          scheduleReconnect()
        }
      }, delay)
    })
  }

  const listenNotifications = async (
    messageType: number,
    data: Map<number, any>,
  ) => {
    const listeners = notificationsListeners.get(messageType)
    if (!listeners?.size) return

    const linkKey = agentConfig.linkKey

    if (!linkKey) return

    const payload = await decryptMessagePayload(data, linkKey)

    switch (messageType) {
      case AgentMessageTypes.MeshOutNotification:
        listeners.forEach(it => it(payload as MeshOutNotification))
        break
      case AgentMessageTypes.MASpaceMessageNotificationType:
        listeners.forEach(it => it(payload))
        break
      default:
    }
  }

  const decryptMessagePayload = async (
    data: Map<number, any>,
    linkKey?: CryptoKey,
  ): Promise<Map<number, any>> => {
    let payloadData = data.get(1) as Uint8Array | undefined
    if (!payloadData) return new Map<number, any>()

    if (linkKey && payloadData && payloadData.length > 0) {
      const iv = payloadData.slice(0, 12)
      const ciphertext = payloadData.slice(12)
      const decryptedBuffer = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv },
        linkKey,
        ciphertext,
      )
      payloadData = new Uint8Array(decryptedBuffer)
    }

    return decode<Map<number, any>>(payloadData)
  }

  const sendMessageReceiveReply = async (
    message: MessageToSend,
    linkKey?: CryptoKey,
  ): Promise<CborMap> => {
    if (!dispatcher) {
      throw new Error('Dispatcher not initialized')
    }
    let reply = await dispatcher.send<Map<number, any>>(
      message.messageId,
      message.payload,
    )
    const messageHeader = reply.get(0) as Map<number, any>
    const messageType = messageHeader.get(2) as number
    const status = messageHeader.get(7) as number
    const statusMessage = messageHeader.get(8)
    const messageId = messageHeader.get(3)

    const responseHeader: ResponseHeader = {
      messageType,
      messageId,
      status,
      statusMessage,
    }

    const responseMap = new Map<number, any>()

    responseMap.set(0, serialize(ResponseHeaderSchema, responseHeader))

    if (status !== AgentStatusCodes.Success) {
      console.warn(
        `Message type ${messageType} failed: status=${status}`,
        statusMessage || '',
      )
      if (
        status === AgentStatusCodes.InvalidSessionIdNeedToReauthenticate ||
        status === AgentStatusCodes.ReauthenticationNeeded
      ) {
        await resetState(true)
        reauthNeededListener?.()
      }

      if (status !== AgentStatusCodes.FormErrorListIsIncluded) {
        return responseMap
      }
    }

    // Header key 15 carries reauthentication timing. Handle both wire formats:
    //  - Old: a raw number of seconds until reauth.
    //  - New: OrgReauthInfo packed map — key 0 = organization_public_id (optional),
    //         key 1 = reauth_expires_at_ms (absolute epoch ms).
    const reauthValue = messageHeader.get(15)
    if (typeof reauthValue === 'number') {
      setReauthenticationTimer(reauthValue)
    } else if (reauthValue instanceof Map) {
      const reauthExpiresAtMs = reauthValue.get(1) as number | undefined
      if (reauthExpiresAtMs !== undefined) {
        const seconds = Math.max(0, (reauthExpiresAtMs - Date.now()) / 1000)
        setReauthenticationTimer(seconds)
      }
    }
    const payload = await decryptMessagePayload(reply, linkKey)

    responseMap.set(1, payload)
    return responseMap
  }

  const pingInterval = setInterval(async () => {
    if (
      socket &&
      socket.readyState === WebSocket.OPEN &&
      agentConfig.linkKey &&
      agentConfig.sessionId
    ) {
      try {
        const message = await buildAppPingRequest(
          agentConfig.linkKey,
          agentConfig.sessionId,
        )
        await sendMessageReceiveReply(message, agentConfig.linkKey)
      } catch (err) {
        console.error('Error sending ping:', err)
      }
    }
  }, 30000)

  window.addEventListener('unload', () => {
    clearInterval(pingInterval)
    intentionalClose = true
    if (reconnectTimerId !== null) {
      clearTimeout(reconnectTimerId)
    }
    resetState(false)
  })

  const createAnonymousSession = async () => {
    if (!agentConfig.clientId) {
      throw new Error('ClientId required for anonymous session')
    }

    // Step 1: CreateSession
    console.log('Creating session...')
    const createSessionMessage = await buildCreateSessionRequest(
      agentConfig.clientId,
    )
    const createSessionReply =
      await sendMessageReceiveReply(createSessionMessage)
    const createSessionPayload = createSessionReply.get(1) as
      | Map<number, any>
      | undefined
    if (!createSessionPayload) {
      throw new Error('CreateSession failed: no payload in response')
    }
    const linkSessionId = createSessionPayload.get(0) as Uint8Array
    const linkKeyRaw = createSessionPayload.get(1) as Uint8Array
    if (!linkSessionId || !linkKeyRaw) {
      throw new Error(
        'CreateSession failed: missing link_session_id or link_key',
      )
    }
    const linkKey = await importAesKey(linkKeyRaw)
    agentConfig.sessionId = linkSessionId
    agentConfig.linkKey = linkKey

    // Step 2: AuthenticateSession with anonymous identity
    console.log('Authenticating anonymous session...')
    const authMessage = await buildAnonymousAuthenticateRequest(
      linkSessionId,
      linkKey,
      agentConfig.anonymousIdentityLinkCode,
    )
    const authReply = await sendMessageReceiveReply(authMessage, linkKey)
    const authPayload = authReply.get(1) as Map<number, any> | undefined
    if (authPayload) {
      const challenges = authPayload.get(0) as number[] | undefined
      if (challenges && challenges.length > 0) {
        throw new Error(
          `Anonymous authentication failed: unexpected challenges: ${challenges}`,
        )
      }
      const anonymousLinkCode = authPayload.get(7) as Uint8Array | undefined
      if (anonymousLinkCode) {
        agentConfig.anonymousIdentityLinkCode = anonymousLinkCode
      }
    }

    agentConfig.isAnonymousSession = true
    console.log('Anonymous session created successfully')
  }

  const agent = {
    initialize: async () => {
      if (initializingPromise) return initializingPromise
      initializingPromise = (async () => {
        try {
          agentConfig = (await readAgentConfig()) || {}
          if (!agentConfig.agentId) {
            agentConfig.agentId = getDestinationAgentId()
          }

          while (!socket || socket.readyState !== WebSocket.OPEN) {
            if (reconnectingPromise) {
              await reconnectingPromise
            } else {
              await connectSocket()
              break
            }
          }

          if (!agentConfig.clientId) {
            const message = await buildRegisterClientRequest()
            const responseMap = await sendMessageReceiveReply(message)
            const response = deserialize(
              RegisterClientResponseSchema,
              responseMap,
            )
            agentConfig.clientId = response.payload?.clientId
          }

          // If no session credentials, create an anonymous session
          if (!agentConfig.linkKey || !agentConfig.sessionId) {
            console.log(
              'No session credentials found, creating anonymous session',
            )
            await createAnonymousSession()
          }

          agentConfig.authenticated = !!(
            agentConfig.linkKey && agentConfig.sessionId
          )
          await exportAgentConfig(agentConfig)
        } finally {
          initializingPromise = null
        }
      })()
      return initializingPromise
    },

    buildAndSendMessage: async (params: {
      payload: CborMap
      messageType: number
      messageId: Id
    }): Promise<CborMap> => {
      await agent.initialize()
      if (
        !dispatcher ||
        !agentConfig.linkKey ||
        !agentConfig.sessionId ||
        !agentConfig.authenticated
      ) {
        throw new Error('Agent not initialized')
      }

      const message = await buildMessage(
        params.messageId,
        params.messageType,
        params.payload,
        agentConfig.sessionId!,
        agentConfig.linkKey!,
      )

      const responseCbor = await sendMessageReceiveReply(
        message,
        agentConfig.linkKey!,
      )

      return responseCbor
    },

    isAuthenticated: (): boolean => {
      return !!agentConfig.authenticated && socket !== null
    },

    isAnonymous: (): boolean => {
      return !!agentConfig.isAnonymousSession
    },

    addMeshOutNotificationListener: (listener: MeshOutNotificationListener) => {
      const listeners =
        notificationsListeners.get(AgentMessageTypes.MeshOutNotification) ||
        new Set<(notification: NotificationPayload) => void>()
      listeners.add(listener)
      notificationsListeners.set(
        AgentMessageTypes.MeshOutNotification,
        listeners,
      )
    },

    addSpaceMessageNotificationListener: (
      listener: SpaceMessageNotificationListener,
    ) => {
      const listeners =
        notificationsListeners.get(
          AgentMessageTypes.MASpaceMessageNotificationType,
        ) || new Set<(notification: Map<number, any>) => void>()
      listeners.add(listener)
      notificationsListeners.set(
        AgentMessageTypes.MASpaceMessageNotificationType,
        listeners as Set<(notification: NotificationPayload) => void>,
      )
    },

    setReauthenticationNeededListener: (listener: () => void) => {
      reauthNeededListener = listener
    },

    setReconnectedListener: (listener: () => void) => {
      reconnectedListener = listener
    },
  }
  return agent as MeshAgent
}
